// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import "./App.css";

import { AuthProvider } from "./auth/AuthProvider";
import { AdminProvider } from "./context/AdminContext";

import AnalyticsTracker from "./components/AnalyticsTracker";
import PushConsentModal from "./components/PushConsentModal";

import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import BrowsePage from "./pages/BrowsePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import EmailConfirmationPage from "./pages/EmailConfirmationPage";
import ProfilePage from "./pages/ProfilePage";
import AngebotsPage from "./pages/AngebotsPage";
import AngebotsAnzeigePage from "./pages/AngebotsAnzeigePage";
import ForumsPage from "./pages/ForumsPage";
import ForumMyPage from "./pages/ForumMyPage";
import ForumAbosPage from "./pages/ForumAbosPage";
import ForumMsgPage from "./pages/ForumMsgPage";
import ForumsBeitragsPage from "./pages/ForumsBeitragsPage";
import RezeptPage from "./pages/RezeptPage";
import MeineRezeptPage from "./pages/MeineRezeptPage";
import AdminPage from "./pages/AdminPage";
import AuthActionPage from "./pages/AuthActionPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import LoeschenPage from "./pages/LoeschenPage";
import ProfileOptionsPage from "./pages/ProfileOptionsPage";
import ProtectedRoute from "./components/ProtectedRoute";
import IosBackButton from "./components/IosBackButton";

// 🔔 Push / Auth listener
import {
  initPushAfterLogin,
  shouldOfferPushPrompt,
  markPushPromptOfferedNow,
  initNativePush,
} from "./services/pushDataService";


import { subscribeToAuthChanges } from "./services/authDataService";

import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";


function NativeBackHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const sub = CapacitorApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
      } else {
        CapacitorApp.exitApp();
      }
    });

    return () => {
      sub.then((listener) => listener.remove());
    };
  }, [navigate]);

  return null;
}
function PendingPushNavigationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    let tries = 0;

    const runPendingNavigation = () => {
      const target = localStorage.getItem("ee_pending_push_navigation");
      if (!target) return;

      tries++;

      console.log("🚀 Versuche Navigation:", target, "Try:", tries);

      // 👉 Navigation versuchen
      navigate(target);

      // 👉 nach Erfolg löschen
      localStorage.removeItem("ee_pending_push_navigation");
    };

    // 🔥 Wiederholt versuchen (wichtig für Resume!)
    const interval = setInterval(() => {
      runPendingNavigation();

      if (tries > 5) {
        clearInterval(interval);
      }
    }, 300);

    // 👉 zusätzlich auf Event reagieren
    const onReady = () => {
      setTimeout(runPendingNavigation, 100);
    };

    window.addEventListener("ee-push-navigation-ready", onReady);

    return () => {
      clearInterval(interval);
      window.removeEventListener("ee-push-navigation-ready", onReady);
    };
  }, [navigate]);

  return null;
}

function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const consumeDeepLink = (url, source = "unknown") => {
      if (!url) return;

      try {
        const parsedUrl = new URL(url);
        const path = parsedUrl.pathname;
        const search = parsedUrl.search || "";
        const target = `${path}${search}`;

        const allowed =
          path.startsWith("/angebot/") ||
          path.startsWith("/forum/beitrag/");

        if (!allowed) return;

        const consumeKey = `ee_consumed_deeplink_${url}`;

        if (sessionStorage.getItem(consumeKey) === "1") {
          console.log("🔗 Deep Link bereits verarbeitet:", url);
          return;
        }

        sessionStorage.setItem(consumeKey, "1");

        console.log("🔗 Deep Link Navigation:", {
          source,
          url,
          target,
        });

        setTimeout(() => {
          navigate(target, { replace: false });
        }, 150);
      } catch (error) {
        console.warn("Deep Link konnte nicht verarbeitet werden:", error);
      }
    };

    let listenerHandle = null;

    CapacitorApp.getLaunchUrl()
      .then((result) => {
        if (result?.url) {
          consumeDeepLink(result.url, "launch");
        }
      })
      .catch((error) => {
        console.warn("getLaunchUrl fehlgeschlagen:", error);
      });

    CapacitorApp.addListener("appUrlOpen", (event) => {
      consumeDeepLink(event.url, "appUrlOpen");
    }).then((handle) => {
      listenerHandle = handle;
    });

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate]);

  return null;
}

function App() {
  const [showPushModal, setShowPushModal] = useState(false);

  // 🌍 vh-Fix für Mobile Browser (Safari / Chrome)
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
    window.addEventListener("resize", setVH);
    return () => window.removeEventListener("resize", setVH);
  }, []);

useEffect(() => {
  if (Capacitor.isNativePlatform()) {
    document.body.classList.add("native-app");
  }
}, []);

// 🔔 Push nach Login (stabil, nicht nervig; Logik in pushDataService.js)
useEffect(() => {
  let unsubscribeRef = null;

  const init = async () => {
    unsubscribeRef = await subscribeToAuthChanges(async (user) => {
      if (!user?.uid) return;
if (user.emailVerified === false) return;

      if (sessionStorage.getItem("ee_push_prompt_tried") === "1") return;
      sessionStorage.setItem("ee_push_prompt_tried", "1");

      if (Capacitor.isNativePlatform()) {
        try {
          const token = await initNativePush();
          console.log("✅ Native Push Token:", token);
        } catch (e) {
          console.warn("❌ Native Push Init fehlgeschlagen:", e);
        }
        return;
      }

      if ("Notification" in window) {
        try {
          if (Notification.permission === "default") {
            if (!shouldOfferPushPrompt()) return;

            setShowPushModal(true);
            return;
          }

          const res = await initPushAfterLogin({ force: false });
          console.log("🔔 Web Push init result:", res);
        } catch (e) {
          console.warn("🔔 Web Push init failed:", e);
        }
      }
    });
  };

  init();

  return () => {
    if (typeof unsubscribeRef === "function") {
      const maybePromise = unsubscribeRef();
      if (maybePromise?.catch) {
        maybePromise.catch((err) =>
          console.warn("App auth unsubscribe failed:", err)
        );
      }
    }
  };
}, []);

  return (
    <AuthProvider>
      <Router>
        <IosBackButton />
      <AdminProvider>
       
  <NativeBackHandler />
  <PendingPushNavigationHandler />
  <DeepLinkHandler />

  {showPushModal && (
    <PushConsentModal
      onAllow={async () => {
        setShowPushModal(false);
        markPushPromptOfferedNow();

        try {
          const res = await initPushAfterLogin({ force: true });
          console.log("🔔 Web Push init result:", res);
        } catch (e) {
          console.warn("🔔 Web Push init failed:", e);
        }
      }}
      onDeny={() => {
        setShowPushModal(false);
        markPushPromptOfferedNow();
      }}
      onLater={() => {
        setShowPushModal(false);
        markPushPromptOfferedNow();
      }}
    />
  )}

  {/* ✅ GA4 Pageviews für SPA */}
  <AnalyticsTracker />

          <Layout>
            <Routes>
              {/* Core */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/confirmation" element={<EmailConfirmationPage />} />
                 <Route path="/browse" element={<BrowsePage />} />

<Route
  path="/profil"
  element={
    <ProtectedRoute>
      <ProfilePage />
    </ProtectedRoute>
  }
/>
<Route
  path="/profil/optionen"
  element={
    <ProtectedRoute>
      <ProfileOptionsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/admin"
  element={
    <ProtectedRoute>
      <AdminPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/angebot-erstellen"
  element={
    <ProtectedRoute>
      <AngebotsPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/angebot/:angebotId"
  element={
    <ProtectedRoute>
      <AngebotsAnzeigePage />
    </ProtectedRoute>
  }
/>

<Route path="/forum" element={<ForumsPage />} />

<Route
  path="/forum/my"
  element={
    <ProtectedRoute>
      <ForumMyPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/forum/abos"
  element={
    <ProtectedRoute>
      <ForumAbosPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/forum/messages"
  element={
    <ProtectedRoute>
      <ForumMsgPage />
    </ProtectedRoute>
  }
/>

<Route
  path="/forum/beitrag/:id"
  element={<ForumsBeitragsPage />}
/>

<Route path="/rezepte" element={<RezeptPage />} />

<Route
  path="/meine-rezepte"
  element={
    <ProtectedRoute>
      <MeineRezeptPage />
    </ProtectedRoute>
  }
/>
              {/* Firebase Auth Action Handler */}
              <Route path="/auth/action" element={<AuthActionPage />} />
              {/* Für den Playstore */}
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/loeschen" element={<LoeschenPage />} />
            </Routes>
          </Layout>
        
      </AdminProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
